import { useState, useEffect, useCallback, useRef, useSyncExternalStore } from 'react'
import { useLogger } from './useLogger'
import { computeReconnectDelay, shouldResetReconnectAttempts } from './echoReconnect'
import { getRealtimeDisabledReason, isAccessTokenExpired, disconnectEcho, type RealtimeDisabledReason } from '@/services/echoClient'
import i18n from '@/i18n'

// Re-exported: the reconnect policy is defined in echoReconnect.ts (pure,
// testable without React/i18n) but belongs to this hook's public surface.
export { computeReconnectDelay, shouldResetReconnectAttempts }

declare global {
  interface Window {
    Echo: any
  }
}

export let globalIsConnected = false
export let globalConnectionState = 'disconnected'
export let globalError: string | null = null
export let globalDisabledReason: RealtimeDisabledReason = null

const listeners = new Set<() => void>()

function notifyListeners() {
  listeners.forEach(listener => listener())
}

let reconnectAttempt = 0
let reconnectTimer: ReturnType<typeof setTimeout> | null = null
let statusPollTimer: ReturnType<typeof setInterval> | null = null
let statusPollingStarted = false

function resetReconnectAttempts(): void {
  reconnectAttempt = 0
  // A pending backoff belongs to the socket we just tore down: leaving it armed
  // would make it fire on the next tick and reconnect the client that was
  // deliberately disconnected (with stale credentials).
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
}

/**
 * Bounded-backoff reconnect on the EXISTING pusher-js socket. Module-level
 * (not a hook closure) so the status poller can drive it too. There is NO
 * attempt cap: backoff saturates at 30s and keeps trying forever — recovery
 * used to die permanently after 10 attempts until a page reload.
 */
function reconnectEcho(): void {
  if (reconnectTimer) clearTimeout(reconnectTimer)
  if (globalConnectionState === 'connecting' || globalConnectionState === 'connected') return
  const delay = computeReconnectDelay(reconnectAttempt)
  reconnectAttempt++
  globalConnectionState = 'connecting'
  notifyListeners()
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null
    const echo = getEcho()
    if (!echo) return
    if (echo.connector?.pusher) {
      // Soft reconnect on the SAME pusher-js socket: every existing
      // channel subscription survives. (The old disconnect()+connect()
      // cycle built a fresh connector and orphaned them all — and
      // pusher-js reconnects on its own anyway, so a hard cycle was
      // never needed.)
      echo.connector.pusher.connect()
    } else if (!echo.connector) {
      echo.connect()
    }
  }, delay)
}

export function getEcho(): any {
  return window.Echo || null
}

/**
 * laravel-echo 2.4 exposes the pusher-js socket on `connector.pusher` —
 * `connector.connection` does NOT exist. Reading the wrong path made every
 * status poll report 'disconnected' forever (and the reconnect give-up
 * counter could never reset).
 */
function getSocketState(echo: any): string | null {
  const state = echo?.connector?.pusher?.connection?.state
  return typeof state === 'string' ? state : null
}

const REALTIME_DISABLED_I18N_KEYS: Record<string, string> = {
  'not-enabled': 'Realtime_disabled_not_enabled',
  'no-token': 'Realtime_disabled_no_token',
  'missing-config': 'Realtime_disabled_missing_config',
  'bootstrap-error': 'Realtime_disabled_bootstrap_error',
}

/**
 * Why realtime is off, in the words a status surface can show. A build-time
 * configuration failure (placeholder VITE_REVERB_*) is invisible otherwise:
 * the socket simply never exists and the state reads 'disconnected'.
 * Translated at CALL time (not module load) so a language switch applies.
 */
function realtimeDisabledMessage(reason: string): string | null {
  const key = REALTIME_DISABLED_I18N_KEYS[reason]
  return key ? i18n.t(key) : null
}

function pollEchoStatus(): void {
  const echo = getEcho()
  const state = getSocketState(echo)

  if (!echo || !echo.connector || state === null) {
    const reason = getRealtimeDisabledReason()
    const disabledError = reason ? realtimeDisabledMessage(reason) : null

    if (
      globalIsConnected ||
      globalConnectionState !== 'disconnected' ||
      globalError !== disabledError ||
      globalDisabledReason !== reason
    ) {
      globalIsConnected = false
      globalConnectionState = 'disconnected'
      globalError = disabledError
      globalDisabledReason = reason
      notifyListeners()
    }
    return
  }

  let newIsConnected: boolean
  let newConnectionState: string
  let shouldReconnect = false

  if (state === 'connected') {
    // Live-but-unauthorized self-heal: channel auth 403s while pusher stays
    // 'connected' when the token expires mid-session. Tear down so the next
    // initEcho (post re-login) rebuilds with fresh credentials.
    if (isAccessTokenExpired()) {
      disconnectEcho()
      // Tear down the backoff too: a reconnect scheduled before the token
      // expired would otherwise reconnect the disconnected client with stale
      // credentials and re-enter 'connecting'.
      resetReconnectAttempts()
      globalIsConnected = false
      globalConnectionState = 'disconnected'
      globalError = i18n.t('Realtime_disabled_no_token')
      globalDisabledReason = 'no-token'
      notifyListeners()
      return
    }
    newIsConnected = true
    newConnectionState = 'connected'
    if (shouldResetReconnectAttempts('socket-connected', globalIsConnected)) {
      resetReconnectAttempts()
    }
    globalError = null
    globalDisabledReason = null
  } else if (state === 'connecting' || state === 'initialized') {
    newIsConnected = false
    newConnectionState = 'connecting'
  } else if (state === 'unavailable' || state === 'failed') {
    newIsConnected = false
    newConnectionState = 'disconnected'
    // pusher-js has exhausted its own retries. Without this the module's
    // backoff only ran on a browser 'online' event, so a socket that failed
    // while connectivity never changed stayed dead until a page reload.
    shouldReconnect = true
  } else {
    newIsConnected = false
    newConnectionState = state || 'disconnected'
  }

  if (newIsConnected !== globalIsConnected || newConnectionState !== globalConnectionState) {
    globalIsConnected = newIsConnected
    globalConnectionState = newConnectionState
    notifyListeners()
  }

  if (shouldReconnect && typeof navigator !== 'undefined' && navigator.onLine) {
    reconnectEcho()
  }
}

/**
 * The status poller is process-wide (one socket, one poller) but several
 * components subscribe to it. Reference-count the subscriptions so the first
 * consumer to unmount cannot stop polling for everyone: unmounting the
 * dashboard used to kill the app-lifetime poller mounted by
 * SystemInfrastructureProvider, freezing the realtime connection state.
 */
let statusPollingSubscribers = 0

export function startStatusPolling(): void {
  statusPollingSubscribers++
  if (statusPollingStarted) return
  statusPollingStarted = true
  pollEchoStatus()
  statusPollTimer = setInterval(pollEchoStatus, 3000)
}

export function stopStatusPolling(): void {
  statusPollingSubscribers = Math.max(0, statusPollingSubscribers - 1)
  if (statusPollingSubscribers > 0) return
  if (statusPollTimer) {
    clearInterval(statusPollTimer)
    statusPollTimer = null
  }
  statusPollingStarted = false
}

export interface UseEchoReturn {
  echo: any
  isConnected: boolean
  error: string | null
  connectionState: string
  /** Why realtime is not running (build config, no token…) — null when fine. */
  disabledReason: RealtimeDisabledReason
  connect: () => void
  disconnect: () => void
  reconnect: () => void
}

export function useEcho(): UseEchoReturn {
  const logger = useLogger('Echo')
  const [isConnected, setIsConnected] = useState(globalIsConnected)
  const [connectionState, setConnectionState] = useState(globalConnectionState)
  const [error, setError] = useState<string | null>(globalError)
  const [disabledReason, setDisabledReason] = useState<RealtimeDisabledReason>(globalDisabledReason)
  const connectionGuardRef = useRef(false)

  useEffect(() => {
    const handleStoreChange = () => {
      setIsConnected(globalIsConnected)
      setConnectionState(globalConnectionState)
      setError(globalError)
      setDisabledReason(globalDisabledReason)
    }

    listeners.add(handleStoreChange)
    return () => {
      listeners.delete(handleStoreChange)
    }
  }, [])

  const connect = useCallback((): void => {
    if (connectionGuardRef.current) return
    connectionGuardRef.current = true
    // StrictMode double-invokes this effect, and every mounting consumer
    // re-runs connect() (the guard is per hook instance). Without this check
    // the global state machine got forced back to 'connecting' while the
    // socket was already live, desyncing the badges for up to one poll cycle.
    if (getSocketState(getEcho()) === 'connected') {
      // Sync the machine-readable state instead of downgrading it, so the
      // badges don't flicker to 'connecting' on a StrictMode double-invoke
      // or a second consumer mounting while the socket is already live.
      if (!globalIsConnected || globalConnectionState !== 'connected') {
        globalIsConnected = true
        globalConnectionState = 'connected'
        notifyListeners()
      }
      return
    }
    resetReconnectAttempts()
    globalError = null
    globalConnectionState = 'connecting'
    notifyListeners()
    // laravel-echo 2.4's Echo.connect() unconditionally constructs a NEW
    // connector and abandons the current one — with every live channel
    // subscription on it. The Echo built by initEcho() already connects on
    // construction, so only bootstrap a connector when there is none left
    // (e.g. after a full disconnect()).
    if (window.Echo && !window.Echo.connector) {
      window.Echo.connect()
    }
  }, [])

  const disconnect = useCallback((): void => {
    connectionGuardRef.current = false
    window.Echo?.disconnect()
    globalIsConnected = false
    globalConnectionState = 'disconnected'
    notifyListeners()
  }, [])

  const reconnect = useCallback((): void => {
    // Shared with the status poller — see reconnectEcho().
    reconnectEcho()
  }, [])

  const handleOnlineStatus = useCallback((): void => {
    if (navigator.onLine && !globalIsConnected) {
      logger.info('Network restored, attempting to reconnect real-time websockets...')
      // A fresh network means a fresh recovery budget — without this reset a
      // socket that burned attempts before the outage stayed on stale backoff.
      if (shouldResetReconnectAttempts('browser-online', globalIsConnected)) {
        resetReconnectAttempts()
      }
      reconnect()
    }
    // logger intentionally excluded: useLogger returns a new object every
    // render, and an unstable identity here re-runs the mount effect (and
    // its connect guard reset) on every state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reconnect])

  const handleVisibilityChange = useCallback((): void => {
    // Waking a terminal whose socket died while nobody was watching (overnight
    // store terminal) must restart recovery from a clean attempt count — and
    // must actually TRIGGER an attempt, since no poller/online event fires
    // for a socket that died while the tab was hidden.
    if (document.visibilityState === 'visible' && !globalIsConnected) {
      if (shouldResetReconnectAttempts('document-visible', globalIsConnected)) {
        resetReconnectAttempts()
      }
      reconnect()
    }
  }, [reconnect])

  useEffect(() => {
    connect()
    startStatusPolling()
    window.addEventListener('online', handleOnlineStatus)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      connectionGuardRef.current = false
      stopStatusPolling()
      // The reconnect timer stays module-owned on purpose: cancelling it here
      // let any consumer unmounting mid-backoff drop the only recovery
      // attempt, leaving the socket dead until an 'online' event. With no
      // attempt cap, backoff saturates at 30s and recovery keeps trying.
      // Deliberately NOT disconnecting window.Echo here: this hook is
      // mounted by the app-lifetime SystemInfrastructureProvider, and the
      // auth logout flow (disconnectEcho) owns socket teardown.
      window.removeEventListener('online', handleOnlineStatus)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [connect, handleOnlineStatus, handleVisibilityChange])

  return {
    echo: window.Echo,
    isConnected,
    error,
    connectionState,
    disabledReason,
    connect,
    disconnect,
    reconnect,
  }
}

// useSyncExternalStore plumbing for the thin selectors below: one module
// listener set, polled by the shared 3s status poller (reference-counted).
const subscribeToConnectionStore = (onChange: () => void): (() => void) => {
  listeners.add(onChange)
  startStatusPolling()
  return () => {
    listeners.delete(onChange)
    stopStatusPolling()
  }
}

/**
 * Authoritative realtime connectivity for page badges (sales/purchases "Live
 * updates", …). Reads the polled global state owned by useEcho — NOT the
 * stockWebSocket slice, which is only written by listeners that can
 * themselves be dead (a dead socket used to still show as live there).
 */
export function useRealtimeConnected(): boolean {
  return useSyncExternalStore(subscribeToConnectionStore, () => globalIsConnected, () => false)
}

/**
 * Why realtime is off (mirrors `error`, as the machine-readable reason) for
 * status surfaces that want to render their own copy.
 */
export function useRealtimeDisabledReason(): RealtimeDisabledReason {
  return useSyncExternalStore(subscribeToConnectionStore, () => globalDisabledReason, () => null)
}

export default useEcho
